import { create } from 'zustand';
import { blockTypes, sectionTypes, defaultBlockStyles, defaultSectionStyles, gridConfig, dragAnimationConfig, sectionProportions, galleryLayouts, sectionYPositions, sectionHeights, galleryGridConfig } from '../config/constants';

// Generate unique ID
const generateId = (prefix = 'item') => `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Snap to grid helper
const snapToGrid = (value, gridSize) => {
  return Math.round(value / gridSize) * gridSize;
};

// Check collision between two blocks
const checkCollision = (block1, block2) => {
  return !(
    block1.x + block1.width < block2.x ||
    block1.x > block2.x + block2.width ||
    block1.y + block1.height < block2.y ||
    block1.y > block2.y + block2.height
  );
};

// Resolve collision by pushing blocks away
const resolveCollision = (movingBlock, blocks, canvasWidth, canvasHeight) => {
  const collisions = blocks.filter(
    (b) => b.id !== movingBlock.id && checkCollision(movingBlock, b)
  );

  if (collisions.length === 0) return blocks;

  const updatedBlocks = [...blocks];
  const pushDistance = dragAnimationConfig.collisionPushDistance;

  collisions.forEach((collidingBlock) => {
    const index = updatedBlocks.findIndex((b) => b.id === collidingBlock.id);
    if (index === -1) return;

    // Calculate push direction (away from moving block center)
    const movingCenterX = movingBlock.x + movingBlock.width / 2;
    const movingCenterY = movingBlock.y + movingBlock.height / 2;
    const collidingCenterX = collidingBlock.x + collidingBlock.width / 2;
    const collidingCenterY = collidingBlock.y + collidingBlock.height / 2;

    const deltaX = collidingCenterX - movingCenterX;
    const deltaY = collidingCenterY - movingCenterY;

    let newX = collidingBlock.x;
    let newY = collidingBlock.y;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Push horizontally
      newX = deltaX > 0 
        ? collidingBlock.x + pushDistance 
        : collidingBlock.x - pushDistance;
    } else {
      // Push vertically
      newY = deltaY > 0 
        ? collidingBlock.y + pushDistance 
        : collidingBlock.y - pushDistance;
    }

    // Keep within canvas bounds
    newX = Math.max(0, Math.min(newX, canvasWidth - collidingBlock.width));
    newY = Math.max(0, Math.min(newY, canvasHeight - collidingBlock.height));

    updatedBlocks[index] = { ...collidingBlock, x: newX, y: newY };
  });

  return updatedBlocks;
};

// Check overlap allowing touching edges (used for gallery compaction)
const hasAreaOverlap = (a, b) => {
  return !(
    a.x + a.width <= b.x ||
    a.x >= b.x + b.width ||
    a.y + a.height <= b.y ||
    a.y >= b.y + b.height
  );
};

const compactItemsAlongAxis = (items, axis) => {
  if (!items.length) return items;

  const minAxis = Math.min(...items.map(item => item[axis]));
  const sorted = [...items].sort((a, b) => a[axis] - b[axis]);
  const placed = new Map();
  const committed = [];

  sorted.forEach((item) => {
    let newPos = item[axis];
    const candidate = { ...item };

    while (newPos > minAxis) {
      candidate[axis] = newPos - 1;
      const overlaps = committed.some(existing => hasAreaOverlap(candidate, existing));
      if (overlaps) {
        break;
      }
      newPos -= 1;
    }

    const finalItem = { ...item, [axis]: newPos };
    committed.push(finalItem);
    placed.set(item.id, finalItem);
  });

  return items.map(item => placed.get(item.id) || item);
};

const useEditorStore = create((set, get) => ({
  // Canvas state - A4 Portrait ratio (210:297mm = 1:√2)
  canvas: {
    width: 794,
    height: 1122, // Changed from 1123 to exact A4 ratio (794 × √2)
    backgroundColor: '#FFFFFF',
    backgroundGradient: null,
    backgroundImage: null,
    globalFont: 'Sarabun', // Default global font
  },
  
  // Grid state
  grid: {
    enabled: true,
    size: gridConfig.defaultSize,
    visible: true,
    snapEnabled: true,
  },
  
  // Blocks (elements on canvas)
  blocks: [],
  
  // Sections (containers for blocks)
  sections: [],
  
  // Initialize default sections
  initializeDefaultSections: () => {
    const canvasHeight = get().canvas.height;
    const canvasWidth = get().canvas.width;
    
    // Create all 4 sections with calculated heights
    const sectionConfigs = [
      { type: sectionTypes.HEADER, order: 0 },
      { type: sectionTypes.GALLERY, order: 1 },
      { type: sectionTypes.DETAIL, order: 2 },
      { type: sectionTypes.FOOTER, order: 3 },
    ];
    
    let currentY = 0;
    const newSections = sectionConfigs.map((config, index) => {
      let sectionHeight;
      let sectionY;
      
      // Use manual override if available, otherwise calculate
      if (sectionHeights[config.type] !== undefined && sectionHeights[config.type] !== null) {
        sectionHeight = sectionHeights[config.type];
      } else if (index === sectionConfigs.length - 1) {
        // Last section takes remaining space
        sectionHeight = canvasHeight - currentY;
      } else {
        // Use proportion
        sectionHeight = Math.round(canvasHeight * sectionProportions[config.type]);
      }
      
      // Use manual Y position if available, otherwise use currentY
      if (sectionYPositions[config.type] !== undefined && sectionYPositions[config.type] !== null) {
        sectionY = sectionYPositions[config.type];
      } else {
        sectionY = currentY;
      }
      
      const section = {
        id: generateId('section'),
        type: config.type,
        x: 0,
        y: sectionY,
        width: canvasWidth,
        height: sectionHeight,
        zIndex: config.order,
        styles: {
          ...defaultSectionStyles[config.type],
          // Default header styles
          ...(config.type === sectionTypes.HEADER && {
            layout: 'logo-text',
            title: 'หัวข้อโปสเตอร์',
            organization: 'สำนักงานใหญ่',
            titleFont: 'Kanit',
            titleSize: 50,
            titleColor: '#D041E6',
            orgFont: 'Sarabun',
            orgSize: 30,
            orgColor: '#414141',
            useCustomOrg: false,
            logoUrl: null,
          }),
          // Default gallery styles
          ...(config.type === sectionTypes.GALLERY && {
            galleryTitle: 'หัวข้อย่อย',
            galleryItems: [],
            galleryGrid: { ...galleryGridConfig, gap: 0 },
            selectedLayout: null,
            galleryBorderRadius: 0,
            galleryBorderWidth: 0,
            galleryBorderColor: '#D041E6',
          }),
          // Default detail styles
          ...(config.type === sectionTypes.DETAIL && {
            detailText: 'เขียนรายละเอียดที่นี่...',
            detailFont: 'Sarabun',
            detailSize: 16,
            detailColor: '#414141',
            detailAlign: 'left',
            detailLineHeight: 1.8,
          }),
        },
      };
      
      currentY += sectionHeight;
      return section;
    });
    
    // Debug: Log section positions
    console.log('Section layout:', newSections.map(s => ({
      type: s.type,
      y: s.y,
      height: s.height,
      bottom: s.y + s.height
    })));
    
    set({ sections: newSections, selectedSectionId: newSections[0].id });
  },
  
  // Selected block ID
  selectedBlockId: null,
  
  // Selected section ID
  selectedSectionId: null,
  
  // Zoom and pan
  zoom: 0.7,
  pan: { x: 0, y: 0 },
  
  // UI State
  showSectionOverlay: false,
  showPropertiesPanel: false, // For mobile

  togglePropertiesPanel: () =>
    set((state) => ({
      showPropertiesPanel: !state.showPropertiesPanel,
    })),

  // History for undo/redo
  history: [],
  historyIndex: -1,
  
  // Actions
  setCanvasSize: (width, height) => 
    set((state) => ({
      canvas: { ...state.canvas, width, height },
    })),
  
  setCanvasBackground: (color, image = null) =>
    set((state) => ({
      canvas: { ...state.canvas, backgroundColor: color, backgroundImage: image, backgroundGradient: null },
    })),

  setCanvasGradient: (gradient) =>
    set((state) => ({
      canvas: { ...state.canvas, backgroundGradient: gradient, backgroundImage: null },
    })),

  setGlobalFont: (font) =>
    set((state) => ({
      canvas: { ...state.canvas, globalFont: font },
    })),
  
  setGridSize: (size) =>
    set((state) => ({
      grid: { ...state.grid, size },
    })),
  
  toggleGrid: () =>
    set((state) => ({
      grid: { ...state.grid, visible: !state.grid.visible },
    })),
  
  toggleSnap: () =>
    set((state) => ({
      grid: { ...state.grid, snapEnabled: !state.grid.snapEnabled },
    })),
  
  toggleSectionOverlay: () =>
    set((state) => ({
      showSectionOverlay: !state.showSectionOverlay,
    })),
  
  addBlock: (type, position = { x: 100, y: 100 }, parentSectionId = null) => {
    const canvas = get().canvas;
    const isFullWidth = type === blockTypes.LOGO || type === blockTypes.TEXT || 
                        type === blockTypes.FRAME || type === blockTypes.DETAIL;
    
    const newBlock = {
      id: generateId('block'),
      type,
      x: isFullWidth ? 0 : position.x,
      y: position.y,
      width: isFullWidth ? canvas.width : (type === blockTypes.IMAGE ? 200 : 150),
      height: type === blockTypes.TEXT || type === blockTypes.DETAIL ? 60 : 150,
      rotation: 0,
      zIndex: get().blocks.length,
      styles: { 
        ...defaultBlockStyles[type],
        // Use global font for text blocks if available
        ...((type === blockTypes.TEXT || type === blockTypes.DETAIL) && { fontFamily: get().canvas.globalFont })
      },
      content: (type === blockTypes.TEXT || type === blockTypes.DETAIL) ? 'ข้อความใหม่' : null,
      parentSectionId,
    };
    
    set((state) => ({
      blocks: [...state.blocks, newBlock],
      selectedBlockId: newBlock.id,
    }));
  },
  
  updateBlock: (id, updates) =>
    set((state) => {
      const { grid, blocks, canvas } = state;
      let updatedBlocks = blocks.map((block) => {
        if (block.id !== id) return block;
        
        const updated = { ...block, ...updates };
        
        // Apply grid snapping if enabled and position/size changed
        if (grid.snapEnabled) {
          if (updates.x !== undefined) {
            updated.x = snapToGrid(updates.x, grid.size);
          }
          if (updates.y !== undefined) {
            updated.y = snapToGrid(updates.y, grid.size);
          }
          if (updates.width !== undefined) {
            updated.width = Math.max(grid.size, snapToGrid(updates.width, grid.size));
          }
          if (updates.height !== undefined) {
            updated.height = Math.max(grid.size, snapToGrid(updates.height, grid.size));
          }
        }
        
        return updated;
      });
      
      // Check collisions and resolve
      const movedBlock = updatedBlocks.find(b => b.id === id);
      if (movedBlock && (updates.x !== undefined || updates.y !== undefined)) {
        updatedBlocks = resolveCollision(movedBlock, updatedBlocks, canvas.width, canvas.height);
      }
      
      return { blocks: updatedBlocks };
    }),
  
  updateBlockStyles: (id, styles) =>
    set((state) => ({
      blocks: state.blocks.map((block) =>
        block.id === id
          ? { ...block, styles: { ...block.styles, ...styles } }
          : block
      ),
    })),
  
  deleteBlock: (id) =>
    set((state) => ({
      blocks: state.blocks.filter((block) => block.id !== id),
      selectedBlockId: state.selectedBlockId === id ? null : state.selectedBlockId,
    })),
  
  selectBlock: (id) =>
    set({ selectedBlockId: id }),
  
  deselectBlock: () =>
    set({ selectedBlockId: null }),
  
  bringToFront: (id) =>
    set((state) => {
      const maxZ = Math.max(...state.blocks.map(b => b.zIndex));
      return {
        blocks: state.blocks.map((block) =>
          block.id === id ? { ...block, zIndex: maxZ + 1 } : block
        ),
      };
    }),
  
  sendToBack: (id) =>
    set((state) => {
      const minZ = Math.min(...state.blocks.map(b => b.zIndex));
      return {
        blocks: state.blocks.map((block) =>
          block.id === id ? { ...block, zIndex: minZ - 1 } : block
        ),
      };
    }),
  
  duplicateBlock: (id) => {
    const block = get().blocks.find(b => b.id === id);
    if (!block) return;
    
    const newBlock = {
      ...block,
      id: generateId('block'),
      x: block.x + 20,
      y: block.y + 20,
      zIndex: get().blocks.length,
    };
    
    set((state) => ({
      blocks: [...state.blocks, newBlock],
      selectedBlockId: newBlock.id,
    }));
  },
  
  setZoom: (zoom) =>
    set({ zoom: Math.max(0.1, Math.min(3, zoom)) }),
  
  setPan: (x, y) =>
    set({ pan: { x, y } }),
  
  resetView: () =>
    set({ zoom: 1, pan: { x: 0, y: 0 } }),
  
  clearCanvas: () =>
    set({ blocks: [], sections: [], selectedBlockId: null, selectedSectionId: null }),
  
  // Section actions
  addSection: (type, position = { x: 100, y: 100 }) => {
    const canvas = get().canvas;
    const state = get();
    
    // Calculate position by stacking all existing sections
    const sectionOrder = [
      sectionTypes.HEADER,
      sectionTypes.GALLERY,
      sectionTypes.DETAIL,
      sectionTypes.FOOTER
    ];
    
    // Get existing sections sorted by type order
    const existingSections = [...state.sections].sort((a, b) => {
      const aIndex = sectionOrder.indexOf(a.type);
      const bIndex = sectionOrder.indexOf(b.type);
      return aIndex - bIndex;
    });
    
    // Calculate y position (stack on top of all existing sections)
    let y = 0;
    for (const section of existingSections) {
      y += section.height;
    }
    
    // Calculate height based on how many sections exist
    const canvasHeight = canvas.height;
    const numExistingSections = existingSections.length;
    const numTotalSections = 4; // Header, Gallery, Detail, Footer
    
    let sectionHeight;
    
    // If this is the last section, use remaining space
    if (numExistingSections === numTotalSections - 1) {
      sectionHeight = canvasHeight - y;
    } else {
      // Otherwise, use proportion
      sectionHeight = Math.round(canvasHeight * sectionProportions[type]);
    }
    
    const newSection = {
      id: generateId('section'),
      type,
      x: 0,
      y,
      width: canvas.width,
      height: sectionHeight,
      zIndex: get().sections.length,
      styles: { 
        ...defaultSectionStyles[type],
        // Header-specific
        layout: type === sectionTypes.HEADER ? 'logo-text' : undefined,
        // Gallery-specific
        galleryItems: type === sectionTypes.GALLERY ? [] : undefined,
        galleryGrid: type === sectionTypes.GALLERY ? {
          ...galleryGridConfig,
          columns: 3,
          rows: 2,
        } : undefined,
      },
    };
    
    set((state) => {
      // Add new section
      const allSections = [...state.sections, newSection];
      
      // Re-sort sections by type order
      const sectionOrder = [
        sectionTypes.HEADER,
        sectionTypes.GALLERY,
        sectionTypes.DETAIL,
        sectionTypes.FOOTER
      ];
      
      const sortedSections = allSections.sort((a, b) => {
        const aIndex = sectionOrder.indexOf(a.type);
        const bIndex = sectionOrder.indexOf(b.type);
        return aIndex - bIndex;
      });
      
      // Recalculate heights for all sections based on proportions
      const canvasHeight = state.canvas.height;
      const numSections = sortedSections.length;
      
      let currentY = 0;
      const repositionedSections = sortedSections.map((section, index) => {
        // Calculate height based on proportion
        let sectionHeight;
        
        if (index === numSections - 1) {
          // Last section takes remaining space
          sectionHeight = canvasHeight - currentY;
        } else {
          // Use proportion
          sectionHeight = Math.round(canvasHeight * sectionProportions[section.type]);
        }
        
        const repositioned = { 
          ...section, 
          y: currentY, 
          x: 0, 
          width: state.canvas.width,
          height: sectionHeight
        };
        currentY += sectionHeight;
        return repositioned;
      });
      
      return {
        sections: repositionedSections,
        selectedSectionId: newSection.id,
        selectedBlockId: null,
      };
    });
  },
  
  updateSection: (id, updates) =>
    set((state) => {
      const { grid } = state;
      
      // Check if height is being updated
      const isHeightChange = updates.height !== undefined;
      
      // First, update the target section
      const updatedSections = state.sections.map((section) => {
        if (section.id !== id) return section;
        
        const updated = { ...section, ...updates };
        
        // Lock all sections: x = 0, width = canvas.width
        updated.x = 0;
        updated.width = state.canvas.width;
        
        // Apply grid snapping for height if enabled
        if (grid.snapEnabled && updates.height !== undefined) {
          updated.height = Math.max(grid.size * 3, snapToGrid(updates.height, grid.size));
        } else if (updates.height !== undefined) {
          updated.height = Math.max(50, updates.height);
        }
        
        return updated;
      });
      
      // Only reposition if height changed
      if (!isHeightChange) {
        return { sections: updatedSections };
      }
      
      // Auto-reposition all sections to stack vertically
      const sectionOrder = [
        sectionTypes.HEADER,
        sectionTypes.GALLERY,
        sectionTypes.DETAIL,
        sectionTypes.FOOTER
      ];
      
      // Sort sections by type order
      const sortedSections = [...updatedSections].sort((a, b) => {
        const aIndex = sectionOrder.indexOf(a.type);
        const bIndex = sectionOrder.indexOf(b.type);
        return aIndex - bIndex;
      });
      
      // Reposition sections to stack vertically
      let currentY = 0;
      const repositionedSections = sortedSections.map(section => {
        const repositioned = { ...section, y: currentY };
        currentY += section.height;
        return repositioned;
      });
      
      console.log('Repositioned sections after update:', repositionedSections.map(s => ({
        type: s.type,
        y: s.y,
        height: s.height
      })));
      
      return { sections: repositionedSections };
    }),
  
  // Gallery item management
  addGalleryItem: (sectionId, imageUrl = null) => {
    set((state) => {
      const section = state.sections.find(s => s.id === sectionId);
      if (!section || section.type !== sectionTypes.GALLERY) return state;
      
      const grid = section.styles.galleryGrid || galleryGridConfig;
      const existingItems = section.styles.galleryItems || [];
      
      // Find next available position (no gap)
      let x = 0;
      let y = 0;
      const itemWidth = 150;
      const itemHeight = 150;
      
      // Simple auto-placement logic
      if (existingItems.length > 0) {
        const lastItem = existingItems[existingItems.length - 1];
        x = lastItem.x + lastItem.width;
        y = lastItem.y;
        
        // Wrap to next row if needed
        if (x + itemWidth > section.width) {
          x = 0;
          y = lastItem.y + lastItem.height;
        }
      }
      
      const newItem = {
        id: generateId('gallery-item'),
        x,
        y,
        width: itemWidth,
        height: itemHeight,
        imageUrl,
        caption: '',
      };
      
      return {
        sections: state.sections.map(s =>
          s.id === sectionId
            ? {
                ...s,
                styles: {
                  ...s.styles,
                  galleryItems: [...existingItems, newItem],
                },
              }
            : s
        ),
      };
    });
  },
  
  updateGalleryItem: (sectionId, itemId, updates) => {
    // Round all numeric position/size values to prevent subpixel rendering gaps
    const roundedUpdates = { ...updates };
    if (roundedUpdates.x !== undefined) roundedUpdates.x = Math.round(roundedUpdates.x);
    if (roundedUpdates.y !== undefined) roundedUpdates.y = Math.round(roundedUpdates.y);
    if (roundedUpdates.width !== undefined) roundedUpdates.width = Math.round(roundedUpdates.width);
    if (roundedUpdates.height !== undefined) roundedUpdates.height = Math.round(roundedUpdates.height);
    
    set((state) => ({
      sections: state.sections.map(s => {
        if (s.id !== sectionId) return s;
        
        return {
          ...s,
          styles: {
            ...s.styles,
            galleryItems: (s.styles.galleryItems || []).map(item =>
              item.id === itemId ? { ...item, ...roundedUpdates } : item
            ),
          },
        };
      }),
    }));
  },
  
  deleteGalleryItem: (sectionId, itemId) => {
    set((state) => ({
      sections: state.sections.map(s => {
        if (s.id !== sectionId) return s;
        
        return {
          ...s,
          styles: {
            ...s.styles,
            galleryItems: (s.styles.galleryItems || []).filter(item => item.id !== itemId),
          },
        };
      }),
    }));
  },
  
  // Apply gallery layout template
  applyGalleryLayout: (sectionId, layoutId) => {
    const layout = galleryLayouts.find(l => l.id === layoutId);
    if (!layout) return;
    
    set((state) => ({
      sections: state.sections.map(s => {
        if (s.id !== sectionId) return s;
        
        // Create gallery items from layout slots
        const existingItems = s.styles.galleryItems || [];
        let newItems = layout.slots.map((slot, index) => {
          // Try to reuse existing image if available
          const existingItem = existingItems[index];
          
          return {
            id: generateId('gallery-item'),
            slotId: slot.id,
            x: Math.round(slot.x),
            y: Math.round(slot.y),
            width: Math.round(slot.width),
            height: Math.round(slot.height),
            imageUrl: existingItem?.imageUrl || null,
            caption: existingItem?.caption || '',
          };
        });
        
        // DISABLED: Compact to eliminate preset gaps (horizontal then vertical)
        // newItems = compactItemsAlongAxis(newItems, 'x');
        // newItems = compactItemsAlongAxis(newItems, 'y');
        
        return {
          ...s,
          styles: {
            ...s.styles,
            selectedLayout: layoutId,
            galleryItems: newItems,
            galleryGrid: { ...(s.styles.galleryGrid || galleryGridConfig), gap: 0 },
          },
        };
      }),
    }));
  },
  
  deleteSection: (id) =>
    set((state) => {
      const remainingSections = state.sections.filter((s) => s.id !== id);
      
      // If no sections left, just delete and return
      if (remainingSections.length === 0) {
        return {
          sections: [],
          blocks: state.blocks.filter((block) => block.parentSectionId !== id),
          selectedSectionId: null,
          selectedBlockId: null,
        };
      }
      
      // Re-sort sections by type order
      const sectionOrder = [
        sectionTypes.HEADER,
        sectionTypes.GALLERY,
        sectionTypes.DETAIL,
        sectionTypes.FOOTER
      ];
      
      const sortedSections = remainingSections.sort((a, b) => {
        const aIndex = sectionOrder.indexOf(a.type);
        const bIndex = sectionOrder.indexOf(b.type);
        return aIndex - bIndex;
      });
      
      // Recalculate heights for all remaining sections
      const canvasHeight = state.canvas.height;
      const numSections = sortedSections.length;
      
      let currentY = 0;
      const repositionedSections = sortedSections.map((section, index) => {
        // Calculate height based on proportion
        let sectionHeight;
        
        if (index === numSections - 1) {
          // Last section takes remaining space
          sectionHeight = canvasHeight - currentY;
        } else {
          // Use proportion
          sectionHeight = Math.round(canvasHeight * sectionProportions[section.type]);
        }
        
        const repositioned = { 
          ...section, 
          y: currentY, 
          x: 0, 
          width: state.canvas.width,
          height: sectionHeight
        };
        currentY += sectionHeight;
        return repositioned;
      });
      
      return {
        sections: repositionedSections,
        blocks: state.blocks.filter((block) => block.parentSectionId !== id),
        selectedSectionId: state.selectedSectionId === id ? null : state.selectedSectionId,
      };
    }),
  
  removeSection: (id) => get().deleteSection(id),

  duplicateSection: (id) =>
    set((state) => {
      const section = state.sections.find(s => s.id === id);
      if (!section) return state;

      const newSection = {
        ...section,
        id: generateId('section'),
        styles: JSON.parse(JSON.stringify(section.styles)), // Deep copy styles
      };

      const index = state.sections.findIndex(s => s.id === id);
      const newSections = [...state.sections];
      newSections.splice(index + 1, 0, newSection);

      // Recalculate layout
      const canvasHeight = state.canvas.height;
      let currentY = 0;
      const repositionedSections = newSections.map((sect, idx) => {
        // Recalculate height if needed, or keep existing height
        // For simplicity, let's keep existing heights but scale if they overflow?
        // Or just stack them.
        
        // Let's just stack them for now.
        const repositioned = { ...sect, y: currentY };
        currentY += sect.height;
        return repositioned;
      });

      return {
        sections: repositionedSections,
        selectedSectionId: newSection.id,
      };
    }),

  moveSectionOrder: (id, direction) =>
    set((state) => {
      const index = state.sections.findIndex(s => s.id === id);
      if (index === -1) return state;
      if (direction === 'up' && index === 0) return state;
      if (direction === 'down' && index === state.sections.length - 1) return state;

      const newSections = [...state.sections];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      // Swap
      [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];

      // Recalculate Y positions
      let currentY = 0;
      const repositionedSections = newSections.map(sect => {
        const repositioned = { ...sect, y: currentY };
        currentY += sect.height;
        return repositioned;
      });

      return { sections: repositionedSections };
    }),

  setSelectedSection: (section) =>
    set({ selectedSectionId: section?.id || null, selectedBlockId: null }),
  
  deselectSection: () =>
    set({ selectedSectionId: null }),
  
  // Check if a block is inside a section
  checkBlockInSection: (block, section) => {
    return (
      block.x >= section.x &&
      block.x + block.width <= section.x + section.width &&
      block.y >= section.y &&
      block.y + block.height <= section.y + section.height
    );
  },
  
  // Get selected section
  getSelectedSection: () => {
    const { sections, selectedSectionId } = get();
    return sections.find(s => s.id === selectedSectionId) || null;
  },
  
  // Get selected block
  getSelectedBlock: () => {
    const { blocks, selectedBlockId } = get();
    return blocks.find(b => b.id === selectedBlockId) || null;
  },
}));

export { useEditorStore };
export default useEditorStore;
